import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconPlugConnected, IconRefresh, IconUnlink } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';

function CrmConnectionsPage() {
  const utils = trpc.useUtils();
  const list = trpc.contacts.crm.list.useQuery();
  const [open, h] = useDisclosure(false);

  const [provider, setProvider] = useState<'hubspot' | 'salesforce' | 'zoho'>('hubspot');
  const [token, setToken] = useState('');

  const connect = trpc.contacts.crm.connect.useMutation({
    onSuccess: () => {
      void utils.contacts.crm.list.invalidate();
      h.close();
      setToken('');
      notifications.show({ message: 'CRM connected', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const disconnect = trpc.contacts.crm.disconnect.useMutation({
    onSuccess: () => void utils.contacts.crm.list.invalidate(),
  });
  const pull = trpc.contacts.crm.pullNow.useMutation({
    onSuccess: (r) => {
      void utils.contacts.crm.list.invalidate();
      void utils.contacts.contacts.list.invalidate();
      notifications.show({
        message: `Pulled ${r.upserts} contacts (${r.failed} failed)`,
        color: r.failed > 0 ? 'yellow' : 'green',
      });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const test = trpc.contacts.crm.test.useMutation({
    onSuccess: (r) => {
      if (r.ok) {
        notifications.show({
          message: 'Connection OK — token is valid.',
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Connection test failed',
          message: r.error,
          color: 'red',
          autoClose: 8000,
        });
      }
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={14} />}
            component={Link}
            to="/vendor/contacts"
          >
            Contacts
          </Button>
        </Group>
        <Group justify="space-between">
          <Title order={2}>CRM connections</Title>
          <Button leftSection={<IconPlugConnected size={14} />} onClick={h.open}>
            Connect a CRM
          </Button>
        </Group>

        <Alert color="blue">
          Bidirectional sync. Inbound: contacts pulled every 15 min and upserted by email. Outbound:
          every WhatsApp / Messenger / IG message becomes a CRM engagement event. HubSpot is the v1
          connector; Salesforce + Zoho follow the same shape.
        </Alert>

        {list.isPending ? (
          <Skeleton height={200} />
        ) : (list.data ?? []).length === 0 ? (
          <Card>
            <Text c="dimmed" ta="center" py="md">
              No CRMs connected yet.
            </Text>
          </Card>
        ) : (
          <Card p={0}>
            <Table striped withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Provider</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Last sync</Table.Th>
                  <Table.Th>Recent events</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(list.data ?? []).map((c) => (
                  <Table.Tr key={c.id}>
                    <Table.Td>
                      <Badge variant="light">{c.provider}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={c.status === 'active' ? 'green' : 'gray'} variant="light">
                        {c.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {c.lastSyncAt ? new Date(c.lastSyncAt).toLocaleString() : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        {c.recentEvents.slice(0, 3).map((e) => (
                          <Text key={e.id} size="xs" c="dimmed">
                            {e.direction} · {e.entityType} ·{' '}
                            <Badge
                              size="xs"
                              variant="light"
                              color={
                                e.status === 'ok'
                                  ? 'green'
                                  : e.status === 'partial'
                                    ? 'yellow'
                                    : 'red'
                              }
                            >
                              {e.entityCount}
                            </Badge>
                          </Text>
                        ))}
                      </Stack>
                    </Table.Td>
                    <Table.Td align="right">
                      <Group gap={4} justify="flex-end">
                        <Button
                          variant="subtle"
                          size="compact-xs"
                          loading={test.isPending && test.variables?.connectionId === c.id}
                          onClick={() => test.mutate({ connectionId: c.id })}
                        >
                          Test
                        </Button>
                        <ActionIcon
                          variant="subtle"
                          onClick={() => pull.mutate({ connectionId: c.id })}
                        >
                          <IconRefresh size={14} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => disconnect.mutate({ connectionId: c.id })}
                        >
                          <IconUnlink size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        )}
      </Stack>

      <Modal opened={open} onClose={h.close} title="Connect a CRM" centered>
        <Stack>
          <Select
            label="Provider"
            data={[
              { value: 'hubspot', label: 'HubSpot' },
              { value: 'salesforce', label: 'Salesforce (coming soon)', disabled: true },
              { value: 'zoho', label: 'Zoho (coming soon)', disabled: true },
            ]}
            value={provider}
            onChange={(v) => setProvider((v as 'hubspot' | 'salesforce' | 'zoho') ?? 'hubspot')}
          />
          <TextInput
            label={provider === 'hubspot' ? 'HubSpot Private App access token' : 'Access token'}
            description={
              provider === 'hubspot'
                ? 'Generate in HubSpot → Settings → Integrations → Private Apps. Needs crm.objects.contacts.read at minimum.'
                : ''
            }
            value={token}
            onChange={(e) => setToken(e.currentTarget.value)}
            type="password"
          />
          <Group justify="end">
            <Button variant="default" onClick={h.close}>
              Cancel
            </Button>
            <Button
              loading={connect.isPending}
              disabled={token.length < 20}
              onClick={() => connect.mutate({ provider, accessToken: token })}
            >
              Connect
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/contacts/crm')({
  component: CrmConnectionsPage,
});
